import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, AlertCircle, ExternalLink } from 'lucide-react';
import { Button } from './ui';
import { useLookupBySrId, useForceReviewBySrId, useReprocessBySrId } from '../hooks/useApi';

// ─── UUID / SR-URL parser (mirrors packages/api/src/utils/parse-sr-application-id.ts)
const UUID_RE = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

function parseInput(raw: string): string | null {
  if (!raw || typeof raw !== 'string') return null;
  let candidate = raw.trim();
  if (!candidate) return null;
  if (candidate.includes('%')) {
    try {
      candidate = decodeURIComponent(candidate);
    } catch {
      // fall through — regex will likely fail
    }
  }
  const match = candidate.match(UUID_RE);
  return match ? match[1].toLowerCase() : null;
}

// ─── Action type
type Action = 'open' | 'force-review' | 'reprocess';

// ─── Variant-specific wrapper classes
const variantClasses: Record<SrLookupVariant, string> = {
  palette: 'w-full',
  navbar: 'flex items-center gap-2',
  page: 'w-full max-w-2xl mx-auto',
};

const containerClasses: Record<SrLookupVariant, string> = {
  palette: 'w-full p-4',
  navbar: 'flex items-center gap-2',
  page: 'w-full rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900',
};

// ─── Props
export type SrLookupVariant = 'palette' | 'navbar' | 'page';

export interface SrLookupProps {
  variant: SrLookupVariant;
  /** Called when the user picks "open" and navigation happens */
  onNavigated?: () => void;
  /** Default action. Defaults to 'open' */
  defaultAction?: Action;
  /** Pre-fill the input and auto-submit if it parses (used by /lookup?sr=... deep-link) */
  initialSrId?: string;
}

// ─── IngestPrompt — shown when the application isn't found locally
interface IngestPromptProps {
  srApplicationId: string;
  onDone?: () => void;
}

function IngestPrompt({ srApplicationId, onDone }: IngestPromptProps) {
  const forceReview = useForceReviewBySrId();
  const [submitted, setSubmitted] = useState(false);
  const [resultMsg, setResultMsg] = useState<string | null>(null);

  const handleIngest = async (mode: 'fetch' | 'best-effort') => {
    try {
      setSubmitted(true);
      await forceReview.mutateAsync({ srApplicationId, mode });
      setResultMsg(
        mode === 'fetch'
          ? 'Fetching and reviewing — check back in a moment.'
          : 'Queued in background. Check the pipeline shortly.'
      );
      onDone?.();
    } catch {
      setResultMsg('Something went wrong. Try again or check the console.');
      setSubmitted(false);
    }
  };

  if (resultMsg) {
    return (
      <div className="mt-3 rounded-md bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900/30 dark:text-green-300">
        {resultMsg}
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
      <p className="mb-3 text-sm font-medium text-amber-900 dark:text-amber-200">
        Not in Recruiter Buddy yet
      </p>
      <p className="mb-4 text-xs text-amber-700 dark:text-amber-400">
        This SR Application ID wasn&apos;t found locally. You can fetch it from SmartRecruiters
        now or queue it in the background.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="primary"
          loading={submitted && forceReview.isPending}
          disabled={submitted}
          onClick={() => handleIngest('fetch')}
        >
          Fetch and review
        </Button>
        <Button
          size="sm"
          variant="outline"
          loading={submitted && forceReview.isPending}
          disabled={submitted}
          onClick={() => handleIngest('best-effort')}
        >
          Queue in background
        </Button>
      </div>
    </div>
  );
}

// ─── Main component
export function SrLookup({ variant, onNavigated, defaultAction = 'open', initialSrId }: SrLookupProps) {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState(initialSrId ?? '');
  const [submittedSrId, setSubmittedSrId] = useState<string | null>(() => {
    if (!initialSrId) return null;
    return parseInput(initialSrId);
  });
  const [parseError, setParseError] = useState<string | null>(null);
  const [action, setAction] = useState<Action>(defaultAction);

  const lookup = useLookupBySrId(submittedSrId);
  const forceReview = useForceReviewBySrId();
  const reprocess = useReprocessBySrId();

  // Navigate when a found application is returned and action = 'open'
  useEffect(() => {
    if (!lookup.data) return;
    if (!lookup.data.found) return;
    const { candidateId } = lookup.data;

    if (action === 'open') {
      navigate(`/people/${candidateId}`);
      onNavigated?.();
    }
    // force-review / reprocess actions are triggered in handleSubmit after lookup settles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookup.data]);

  const handleSubmit = () => {
    setParseError(null);
    const parsed = parseInput(inputValue);
    if (!parsed) {
      setParseError('Not a valid SmartRecruiters application ID or URL.');
      return;
    }

    // If we already have a result for this ID, re-trigger effects
    if (submittedSrId === parsed && lookup.data) {
      triggerAction(parsed, lookup.data);
      return;
    }

    setSubmittedSrId(parsed);
  };

  const triggerAction = async (
    srId: string,
    data: { found: boolean; candidateId?: number }
  ) => {
    if (!data.found) return; // IngestPrompt handles not-found

    const candidateId = (data as { candidateId: number }).candidateId;

    if (action === 'force-review') {
      await forceReview.mutateAsync({ srApplicationId: srId });
      navigate(`/people/${candidateId}`);
      onNavigated?.();
    } else if (action === 'reprocess') {
      await reprocess.mutateAsync({ srApplicationId: srId });
      navigate(`/people/${candidateId}`);
      onNavigated?.();
    } else {
      navigate(`/people/${candidateId}`);
      onNavigated?.();
    }
  };

  const isLoading = lookup.isLoading || forceReview.isPending || reprocess.isPending;
  const showNotFound = submittedSrId && lookup.data && !lookup.data.found;

  const isNavbar = variant === 'navbar';

  return (
    <div className={variantClasses[variant]}>
      <div className={containerClasses[variant]}>
        {/* Title — only for page variant */}
        {variant === 'page' && (
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Look up by SR Application ID
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Paste a SmartRecruiters application URL or UUID to open, review, or reprocess a
              candidate.
            </p>
          </div>
        )}

        {/* Input row */}
        <div className={isNavbar ? 'flex items-center gap-2' : 'flex flex-col gap-2 sm:flex-row'}>
          {/* Input */}
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setParseError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
              }}
              placeholder="Paste SmartRecruiters URL or UUID…"
              aria-label="SmartRecruiters application URL or UUID"
              className={[
                'block w-full rounded-md border py-2 pl-10 pr-3 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-primary-500',
                parseError
                  ? 'border-red-300 bg-red-50 text-red-900 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300'
                  : 'border-gray-300 bg-white text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100',
                'placeholder-gray-400 dark:placeholder-gray-500',
              ].join(' ')}
            />
          </div>

          {/* Action selector — only for non-navbar */}
          {!isNavbar && (
            <select
              value={action}
              onChange={(e) => setAction(e.target.value as Action)}
              aria-label="Action"
              className="rounded-md border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              <option value="open">Open profile</option>
              <option value="force-review">Force AI review</option>
              <option value="reprocess">Reprocess</option>
            </select>
          )}

          {/* Submit button */}
          <Button
            variant="primary"
            size={isNavbar ? 'sm' : 'md'}
            loading={isLoading}
            onClick={handleSubmit}
            leftIcon={isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : undefined}
          >
            {isNavbar ? 'Look up' : action === 'open' ? 'Open' : action === 'force-review' ? 'Force review' : 'Reprocess'}
          </Button>
        </div>

        {/* Inline parse error */}
        {parseError && (
          <div className="mt-2 flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {parseError}
          </div>
        )}

        {/* Lookup error */}
        {lookup.isError && (
          <div className="mt-2 flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            Failed to look up application. Check your connection and try again.
          </div>
        )}

        {/* Not-found ingest prompt */}
        {showNotFound && (
          <IngestPrompt
            srApplicationId={submittedSrId!}
            onDone={() => {
              setSubmittedSrId(null);
              setInputValue('');
            }}
          />
        )}

        {/* Found — show a quick confirmation before navigation */}
        {submittedSrId && lookup.data?.found && (
          <div className="mt-2 flex items-center gap-1.5 text-sm text-green-700 dark:text-green-400">
            <ExternalLink className="h-4 w-4 flex-shrink-0" />
            Application found — navigating…
          </div>
        )}
      </div>
    </div>
  );
}
